/**
 * Convert any value to a number with safe fallback
 */
export function toNumber(x: any): number {
  if (x === null || x === undefined) return 0;
  const n = typeof x === 'string' ? Number(x) : x;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Formats a percentage value with proper sign and styling
 * @param n Percentage value (can be null/undefined)
 * @returns Formatted percentage string with sign
 */
export function formatPct(n?: number | null): string;
export function formatPct(value: any, opts?: { isFraction?: boolean; digits?: number }): string;
export function formatPct(valueOrN: any, opts?: { isFraction?: boolean; digits?: number }): string {
  // Handle overloaded signature
  if (opts === undefined && (typeof valueOrN === 'number' || valueOrN === null || valueOrN === undefined)) {
    // Legacy signature: formatPct(n?: number | null)
    const n = valueOrN;
    if (n === null || n === undefined || typeof n !== 'number' || !Number.isFinite(n)) {
      return '—';
    }
    const sign = n > 0 ? '+' : n < 0 ? '' : '+';
    return `${sign}${n.toFixed(2)}%`;
  }

  // New signature: formatPct(value: any, opts?: { isFraction?: boolean; digits?: number })
  const v = toNumber(valueOrN);
  const isFraction = !!opts?.isFraction;
  const digits = opts?.digits ?? 2;
  const pv = isFraction ? v * 100 : v;
  return `${pv.toFixed(digits)}%`;
}

/**
 * Formats a currency value with proper sign
 * @param amount Amount value
 * @param currency Currency symbol (default: 'USD')
 * @returns Formatted currency string
 */
export function formatCurrency(amount?: number | null, currency: string = 'USD'): string {
  if (amount === null || amount === undefined || typeof amount !== 'number' || !Number.isFinite(amount)) {
    return '—';
  }
  
  const sign = amount >= 0 ? '+' : '';
  return `${sign}${amount.toFixed(2)} ${currency}`;
}

/**
 * Formats a number with proper fallback
 * @param n Number value
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(n?: number | null, decimals: number = 2): string {
  if (n === null || n === undefined || typeof n !== 'number' || !Number.isFinite(n)) {
    return '—';
  }
  
  return n.toFixed(decimals);
}

/**
 * Formats a large number with K/M/B suffixes
 * @param n Number value
 * @returns Formatted number with suffix
 */
export function formatLargeNumber(n?: number | null): string {
  if (n === null || n === undefined || typeof n !== 'number' || !Number.isFinite(n)) {
    return '—';
  }

  if (n >= 1000000000) {
    return `${(n / 1000000000).toFixed(1)}B`;
  }
  if (n >= 1000000) {
    return `${(n / 1000000).toFixed(1)}M`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}K`;
  }

  return n.toFixed(0);
}

/**
 * Format USD amount with optional digits
 */
export function formatUsd(value: any, digits = 2): string {
  const v = toNumber(value);
  const sign = v < 0 ? '-' : '';
  return `${sign}${Math.abs(v).toFixed(digits)} USDT`;
}

/**
 * Format percentage from fraction (0.009 -> 0.90%)
 * Convenient wrapper for formatPct with isFraction: true
 */
export function formatPctFrac(frac: number, digits = 2): string {
  return formatPct(frac, { isFraction: true, digits });
}

/**
 * Format money amount with currency code
 * @param n Amount value
 * @param code Currency code (default: 'USDT')
 * @returns Formatted money string (e.g., "9.12 USDC")
 */
export function formatMoney(n: any, code?: string): string {
  const num = toNumber(n);
  const sym = (code || 'USDT').toUpperCase();
  if (!isFinite(num)) return `0.00 ${sym}`;
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  return `${sign}${abs.toFixed(2)} ${sym}`;
}

/**
 * Format percentage from fraction (0.042 -> 4.20%)
 * Always treats input as fraction (0..1), converts to percentage
 * @param x Fraction value (0.042 = 4.2%)
 * @param digits Decimal places (default: 2)
 * @returns Formatted percentage string
 */
export function pctFromFraction(x?: number | null, digits = 2): string {
  if (x === null || x === undefined) return '0.00%';
  return `${(x * 100).toFixed(digits)}%`;
}

/**
 * Format USD amount with currency code
 * @param x Amount value
 * @param c Currency code (default: 'USDC')
 * @returns Formatted USD string (e.g., "123.45 USDC")
 */
export function usd(x?: number | null, c = 'USDC'): string {
  if (x === null || x === undefined) return `0.00 ${c}`;
  return `${x.toFixed(2)} ${c}`;
}